/**
 * friendlyError — turn a thrown Error (or a raw error message) into
 * user-safe copy for toasts and banners.
 *
 * Why: useV2Api builds Error objects whose `message` may carry the raw
 * server response body (HTML stack trace, plain text, etc). Rendering that
 * verbatim in a toast leaks implementation details and looks broken.
 *
 * The mapping prefers `err.status` (set by useV2Api) when available.
 */

const STATUS_MESSAGES = {
  400: 'That request looked off — please check your input and try again.',
  401: 'Your session expired. Please sign in again.',
  403: 'You do not have permission to perform that action.',
  404: "Couldn't find that resource.",
  408: 'The request timed out. Please try again.',
  409: 'There was a conflict — refresh and try again.',
  413: 'That payload is too large.',
  429: "You're going too fast. Please wait a moment and retry.",
  500: 'Something went wrong on our end. Try again in a moment.',
  502: 'Upstream service is unavailable. Try again in a moment.',
  503: 'Service temporarily unavailable. Try again in a moment.',
  504: 'Upstream timed out. Try again in a moment.'
}

const NETWORK_MESSAGE = "Couldn't connect — check your connection and retry."

/**
 * Map an Error (or string) to user-safe banner copy.
 *
 * @param {unknown} input          The thrown value — typically an Error built by useV2Api.
 * @param {string=} fallbackText   Optional context-specific fallback used when `input`
 *                                 is null/undefined OR when the message would
 *                                 otherwise collapse to the generic 500 copy.
 *                                 Lets call sites preserve their old `'Failed to load X'`
 *                                 strings without bypassing status mapping.
 */
export function friendlyErrorMessage(input, fallbackText) {
  if (!input) return fallbackText || ''
  if (typeof input === 'string') {
    return mapMessage(input, fallbackText)
  }
  if (typeof input?.status === 'number' && STATUS_MESSAGES[input.status]) {
    return STATUS_MESSAGES[input.status]
  }
  if (typeof input?.status === 'number' && input.status >= 500) {
    return STATUS_MESSAGES[500]
  }
  if (typeof input?.status === 'number' && input.status >= 400) {
    return STATUS_MESSAGES[400]
  }
  return mapMessage(input.message || '', fallbackText)
}

function mapMessage(msg, fallbackText) {
  if (!msg) return fallbackText || STATUS_MESSAGES[500]
  const lower = String(msg).toLowerCase()
  if (lower.includes('failed to fetch') || lower.includes('network') || lower.includes('econnrefused')) {
    return NETWORK_MESSAGE
  }
  if (lower.startsWith('http ')) {
    const m = lower.match(/^http (\d{3})/)
    if (m) {
      const status = Number(m[1])
      if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status]
      if (status >= 500) return STATUS_MESSAGES[500]
      if (status >= 400) return STATUS_MESSAGES[400]
    }
  }
  // If the source message looks like raw HTML or a stack trace, fall back to
  // the context-specific copy if the caller supplied one, else the generic 500.
  if (looksLikeHtml(msg) || looksLikeStackTrace(msg)) {
    return fallbackText || STATUS_MESSAGES[500]
  }
  return msg
}

function looksLikeHtml(msg) {
  return /<\/?[a-z][\s\S]*>/i.test(msg)
}

// A stack-trace shape is broadly: multi-line text containing one of the
// common stack-frame markers. Heuristic, but broad enough to catch the
// raw bodies the reviewer flagged (e.g. "TypeError: x at /app/foo.js:32").
function looksLikeStackTrace(msg) {
  const text = String(msg)
  if (!text.includes('\n')) {
    // Single-line: only treat as a stack trace if it explicitly looks like one.
    const lower = text.toLowerCase()
    return lower.includes(' at ') && (lower.includes('.js:') || lower.includes('.ts:'))
  }
  return (
    /\bat\s+\//.test(text) ||
    /:\d+:\d+\b/.test(text) ||
    /\.(?:js|ts|jsx|tsx):\d+/.test(text) ||
    /^\s*(?:Error|TypeError|RangeError|ReferenceError|SyntaxError):/m.test(text)
  )
}
